import { ref, computed, unref, watch } from 'vue';
import { escapeSql } from '@rdrive/helper-sql';
import { makeChartConfig } from '@rdrive/helper-chart-config';
import { useDebounce } from '@rdrive/vue-debounce';

// "Resolution Time" (Analysis page): how long issues took from their first status to the last
// time they reached the selected status, as a box plot per issue category → issue type → issue,
// for @rdrive/plugin-chart-box-plot's BaseBoxPlotChart — IA_ABSORPTION_PLAN Phase 4 step 12d; was
// components/charts/DrawingLocationBoxPlot.vue (the adapter) on components/charts/base/BoxPlot.vue
// (the base), whose query strings these are, VERBATIM apart from the escaping: the selected
// status's title/colour now go through escapeSql (an apostrophe in a status title broke all three
// queries), and the issue-type filter matches a NULL category with `IS NULL` (the old
// `issueCategory.replace(…)` threw on it, so a type without a category could not be clicked).
//
// The card's header controls are this config's too: the status picker (`statusOptions`,
// `selectedStatus`, `setStatus`; it starts on the first status with IsPassed, e.g. "Closed") and
// the filter button's date-range dialog (`settingsDialog`, `statusHistoryDates`,
// `tempStatusHistoryRange`, `getDateFromIndex`, `applySettings`, `cancelSettings`) — the range is
// a filter on the status-history context under its own id, `statusHistoryRangeSlider`, applied
// 500 ms after a change (and once after the load, over every date, as before).
//
// The page renders:
//   <BaseBoxPlotChart v-bind="chart" :dataLoaded="ready" :color="color" :reloadKey="selectedStatus"
//     :filterContext="[issues context, status-history context]" navigation="arrows" skipEmptyLevels>
//     <template #controls> the picker + the filter button </template>
//   </BaseBoxPlotChart>
// `ready` holds the first load back until the statuses and the history dates are known (the old
// base loaded in parallel with them, so its first render queried an empty status — and a colour
// change in that window looked up level `0`: "No data provider found for level: 0").
//
// `filterContext` is the STATUS-HISTORY context: a box click filters it (by category, then
// category + type); the SQL reads `filteredIssues` (the issues context) and, while a date range
// is set, `filteredStatusHistory`. Rows: { title, parentId?, count, min, Q1, median, Q3, max,
// average, standard_deviation } — durations in days.
export function useResolutionTimeChart({ chartName, filterContext, conn, dataLoaded }) {
  const { ctx, rows, applyFilter, removeFilter } = makeChartConfig({ chartName, filterContext, conn });

  // ---- the status picker
  const statusOptions = ref([]);
  const selectedStatus = ref('');
  const statusesReady = ref(false);
  const statusTitle = () => selectedStatus.value?.title || '';
  const statusColor = () => selectedStatus.value?.color || '';
  const setStatus = (newStatus) => {
    selectedStatus.value = newStatus;
  };

  // ---- the date range (the filter button's settings dialog)
  const settingsDialog = ref(false);
  const statusHistoryRange = ref([0, 0]);
  const tempStatusHistoryRange = ref([0, 0]);
  const statusHistoryDates = ref([]);
  const datesReady = ref(false);
  const debouncedStatusHistoryRange = useDebounce(statusHistoryRange, 500);

  // Arrow returns BIGINT counts; the statistics are coerced as before (median is a DOUBLE already)
  const boxRows = async (query) => (await rows(query)).map((jsonRow) => ({
    ...jsonRow,
    count: Number(jsonRow.count),
    min: Number(jsonRow.min),
    max: Number(jsonRow.max),
    Q1: Number(jsonRow.Q1),
    Q3: Number(jsonRow.Q3),
    average: Number(jsonRow.average),
    standard_deviation: Number(jsonRow.standard_deviation)
  }));

  const getIssueStatuses = async () => rows(`
    SELECT DISTINCT
    s.title AS value,
    s.title AS title,
    s.color as color,
    FROM status_templates s
    INNER JOIN issues i ON s.id = i.IssueStatusId
    ORDER BY s.sort
    `);

  const getInitialIsTerminalIsPassedStatusTitle = async () => {
    const result = await rows(`
    SELECT Title
    FROM status_templates s
    INNER JOIN issues i ON s.id = i.IssueStatusId
    WHERE IsPassed = true
    ORDER BY Sort ASC
    LIMIT 1;
    `);
    if (result.length > 0) {
      return result[0].Title;
    }
    return '';
  };

  // Level 1: resolution time per issue category
  const getIssueCategoryData = async () => {
    const table = 'filteredIssues';
    const hasRangeFilter = statusHistoryRange.value && statusHistoryRange.value[0] !== statusHistoryRange.value[1];

    const query = `
      WITH first_status AS (
        -- Get the first status history for each subItemId
        SELECT 
            sh.subItemId,
            MIN(sh.validFrom) as first_status_date
        FROM status_history_temporal sh
        GROUP BY sh.subItemId
        ),
        last_selected_status AS (
        -- Get the last status that matches the selected status (title and color) for each subItemId
        SELECT 
            sh.subItemId,
            MAX(sh.validFrom) as last_selected_date
        FROM status_history_temporal sh
        INNER JOIN status_templates st ON sh.statusId = st.Id
        WHERE st.title = '${escapeSql(statusTitle())}' 
          AND st.color = '${escapeSql(statusColor())}'
        GROUP BY sh.subItemId
        ),
        ${hasRangeFilter ? `issues_in_range AS (
        -- Get issues that have at least one status change in the selected range
        SELECT DISTINCT subItemId
        FROM filteredStatusHistory
        ),` : ''}
        durations AS (
        -- Calculate duration for each subItemId
        SELECT 
            i.id,
            i.issueCategory,
            fs.first_status_date,
            lss.last_selected_date,
            -- Calculate duration in days
            EXTRACT(EPOCH FROM (lss.last_selected_date - fs.first_status_date)) / 86400 as duration_days
        FROM ${table} i
        INNER JOIN first_status fs ON i.id = fs.subItemId
        INNER JOIN last_selected_status lss ON i.id = lss.subItemId
        ${hasRangeFilter ? 'INNER JOIN issues_in_range ir ON i.id = ir.subItemId' : ''}
        WHERE fs.first_status_date IS NOT NULL 
            AND lss.last_selected_date IS NOT NULL
            AND lss.last_selected_date >= fs.first_status_date
        ),
        boxplot_stats AS (
        -- Calculate boxplot statistics for each category
        SELECT 
            issueCategory,
            COUNT(*) as count,
            MIN(duration_days) as min_value,
            PERCENTILE_CONT(0.25) WITHIN GROUP (ORDER BY duration_days) as q1,
            PERCENTILE_CONT(0.50) WITHIN GROUP (ORDER BY duration_days) as median,
            PERCENTILE_CONT(0.75) WITHIN GROUP (ORDER BY duration_days) as q3,
            MAX(duration_days) as max_value,
            AVG(duration_days) as avg_value,
            STDDEV(duration_days) as std_dev
        FROM durations
        WHERE issueCategory IS NOT NULL
        GROUP BY issueCategory
        HAVING COUNT(*) > 0
        )
        SELECT 
        issueCategory as title,
        count,
        ROUND(min_value, 3) as min,
        ROUND(q1, 3) as Q1,
        ROUND(median, 3) as median,
        ROUND(q3, 3) as Q3,
        ROUND(max_value, 3) as max,
        ROUND(avg_value, 3) as average,
        ROUND(std_dev, 3) as standard_deviation
        FROM boxplot_stats
        ORDER BY median DESC;
    `;

    return boxRows(query);
  };

  // Level 2: per issue type (parentId = its category)
  const getIssueTypeData = async () => {
    const table = 'filteredIssues';
    const hasRangeFilter = statusHistoryRange.value && statusHistoryRange.value[0] !== statusHistoryRange.value[1];

    const query = `
      WITH first_status AS (
        -- Get the first status history for each subItemId
        SELECT 
            sh.subItemId,
            MIN(sh.validFrom) as first_status_date
        FROM status_history_temporal sh
        GROUP BY sh.subItemId
      ),
      last_selected_status AS (
        -- Get the last status that matches the selected status (title and color) for each subItemId
        SELECT 
            sh.subItemId,
            MAX(sh.validFrom) as last_selected_date
        FROM status_history_temporal sh
        INNER JOIN status_templates st ON sh.statusId = st.Id
        WHERE st.title = '${escapeSql(statusTitle())}' 
          AND st.color = '${escapeSql(statusColor())}'
        GROUP BY sh.subItemId
      ),
      ${hasRangeFilter ? `issues_in_range AS (
        -- Get issues that have at least one status change in the selected range
        SELECT DISTINCT subItemId
        FROM filteredStatusHistory
      ),` : ''}
      durations AS (
        -- Calculate duration for each subItemId
        SELECT 
          i.id,
          i.issueCategory,
          i.issueType,
          fs.first_status_date,
          lss.last_selected_date,
          -- Calculate duration in days
          EXTRACT(EPOCH FROM (lss.last_selected_date - fs.first_status_date)) / 86400 as duration_days
      FROM ${table} i
        INNER JOIN first_status fs ON i.id = fs.subItemId
        INNER JOIN last_selected_status lss ON i.id = lss.subItemId
        ${hasRangeFilter ? 'INNER JOIN issues_in_range ir ON i.id = ir.subItemId' : ''}
        WHERE fs.first_status_date IS NOT NULL 
            AND lss.last_selected_date IS NOT NULL
            AND lss.last_selected_date >= fs.first_status_date
      ),
      boxplot_stats AS (
        -- Calculate boxplot statistics for each issue type within the selected category
        SELECT 
          issueType,
          issueCategory,
          COUNT(*) as count,
          MIN(duration_days) as min_value,
          PERCENTILE_CONT(0.25) WITHIN GROUP (ORDER BY duration_days) as q1,
          PERCENTILE_CONT(0.50) WITHIN GROUP (ORDER BY duration_days) as median,
          PERCENTILE_CONT(0.75) WITHIN GROUP (ORDER BY duration_days) as q3,
          MAX(duration_days) as max_value,
          AVG(duration_days) as avg_value,
          STDDEV(duration_days) as std_dev
      FROM durations
      WHERE issueType IS NOT NULL
      GROUP BY issueType, issueCategory
      HAVING COUNT(*) > 0
    )
    SELECT 
      issueType as title,
      issueCategory as parentId,
        count,
        ROUND(min_value, 3) as min,
        ROUND(q1, 3) as Q1,
        ROUND(median, 3) as median,
        ROUND(q3, 3) as Q3,
        ROUND(max_value, 3) as max,
        ROUND(avg_value, 3) as average,
        ROUND(std_dev, 3) as standard_deviation
      FROM boxplot_stats
      ORDER BY median DESC;
    `;

    return boxRows(query);
  };

  // Level 3 (terminal — a click there does nothing): one box per issue (parentId = its type)
  const getIssueItemData = async () => {
    const table = 'filteredIssues';
    const hasRangeFilter = statusHistoryRange.value && statusHistoryRange.value[0] !== statusHistoryRange.value[1];

    const query = `
      WITH first_status AS (
        -- Get the first status history for each subItemId
        SELECT 
            sh.subItemId,
            MIN(sh.validFrom) as first_status_date
        FROM status_history_temporal sh
        GROUP BY sh.subItemId
      ),
      last_selected_status AS (
        -- Get the last status that matches the selected status (title and color) for each subItemId
        SELECT 
            sh.subItemId,
            MAX(sh.validFrom) as last_selected_date
        FROM status_history_temporal sh
        INNER JOIN status_templates st ON sh.statusId = st.Id
        WHERE st.title = '${escapeSql(statusTitle())}' 
          AND st.color = '${escapeSql(statusColor())}'
        GROUP BY sh.subItemId
      ),
      ${hasRangeFilter ? `issues_in_range AS (
        -- Get issues that have at least one status change in the selected range
        SELECT DISTINCT subItemId
        FROM filteredStatusHistory
      ),` : ''}
      durations AS (
        -- Calculate duration for each subItemId
        SELECT 
          i.id,
          i.issueCategory,
          i.issueType,
          i.issueItem,
          l.Title as location_title,
          fs.first_status_date,
          lss.last_selected_date,
          -- Calculate duration in days
          EXTRACT(EPOCH FROM (lss.last_selected_date - fs.first_status_date)) / 86400 as duration_days
      FROM ${table} i
      INNER JOIN first_status fs ON i.id = fs.subItemId
      INNER JOIN last_selected_status lss ON i.id = lss.subItemId
      LEFT JOIN locations l ON i.issueLocationId = l.Id
      ${hasRangeFilter ? 'INNER JOIN issues_in_range ir ON i.id = ir.subItemId' : ''}
        WHERE fs.first_status_date IS NOT NULL 
            AND lss.last_selected_date IS NOT NULL
            AND lss.last_selected_date >= fs.first_status_date
      ),
      boxplot_stats AS (
        -- Calculate statistics for each individual issue
        SELECT 
          id,
          COALESCE(issueItem, location_title, id) as issue_item,
          issueType,
          duration_days
      FROM durations
      WHERE issueType IS NOT NULL
      )
      SELECT 
      id,
      issue_item as title,
      issueType as parentId,
        ROUND(duration_days, 3) as value,
        1 as count,
        ROUND(duration_days, 3) as min,
        ROUND(duration_days, 3) as Q1,
        ROUND(duration_days, 3) as median,
        ROUND(duration_days, 3) as Q3,
        ROUND(duration_days, 3) as max,
        ROUND(duration_days, 3) as average,
        0 as standard_deviation
      FROM boxplot_stats
      ORDER BY duration_days DESC;
    `;

    return boxRows(query);
  };

  // ---- context updaters (the status-history context, under `chartName`)
  const categoryIs = (issueCategory) => (issueCategory === null || issueCategory === undefined
    ? 'IS NULL'
    : `= '${escapeSql(issueCategory)}'`);

  const getIssueCategoryFilterContext = async (issueCategory) => {
    const query = `
      SELECT DISTINCT 
        sht.subItemId,
        sht.validFrom,
        sht.statusId
      FROM status_history_temporal sht
      INNER JOIN issues i ON sht.subItemId = i.Id
      WHERE i."BI-Issue-Category" ${categoryIs(issueCategory)}
    `;
    await applyFilter(query);
  };

  const getIssueTypeFilterContext = async (issueType, issueCategory) => {
    const query = `
      SELECT DISTINCT 
        sht.subItemId,
        sht.validFrom,
        sht.statusId
      FROM status_history_temporal sht
      INNER JOIN issues i ON sht.subItemId = i.Id
      WHERE i."BI-Issue-Category" ${categoryIs(issueCategory)}
      AND i."BI-Issue-Type" = '${escapeSql(issueType)}'
    `;
    await applyFilter(query);
  };

  // ---- the date range
  const getDateFromIndex = (index, includeYear = false) => {
    if (statusHistoryDates.value.length === 0 || index < 0 || index >= statusHistoryDates.value.length) return '';

    const date = new Date(statusHistoryDates.value[index]);
    const options = includeYear
      ? { year: 'numeric', month: 'short', day: 'numeric' }
      : { month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const formatDateForSQL = (date) => {
    const validDate = date instanceof Date ? date : new Date(date);
    if (!validDate || isNaN(validDate.getTime())) return '';

    const year = validDate.getFullYear();
    const month = String(validDate.getMonth() + 1).padStart(2, '0');
    const day = String(validDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const applyStatusHistoryRangeFilter = async (newRange) => {
    if (!newRange || !Array.isArray(newRange) || newRange.length !== 2) return;
    if (statusHistoryDates.value.length === 0) return;

    const [startIndex, endIndex] = newRange;

    // Get dates directly from indices, as YYYY-MM-DD
    const startDate = formatDateForSQL(new Date(statusHistoryDates.value[startIndex]));
    const endDate = formatDateForSQL(new Date(statusHistoryDates.value[endIndex]));

    const query = `
      SELECT DISTINCT subItemId, validFrom, statusId
      FROM status_history_temporal
      WHERE CAST(validFrom AS DATE) >= '${escapeSql(startDate)}' AND CAST(validFrom AS DATE) <= '${escapeSql(endDate)}'
    `;

    await ctx().updateFilterWithQuery('statusHistoryRangeSlider', query);
  };

  const loadStatusHistoryDates = async () => {
    try {
      const distinctDatesData = await rows(`
        SELECT DISTINCT CAST(validFrom AS DATE) as date
        FROM status_history_temporal
        ORDER BY date
      `);
      statusHistoryDates.value = distinctDatesData.map((row) => row.date);

      // Initialize the range to cover all dates
      if (statusHistoryDates.value.length > 0) {
        statusHistoryRange.value = [0, statusHistoryDates.value.length - 1];
        tempStatusHistoryRange.value = [...statusHistoryRange.value];
      }
    } catch (error) {
      console.error("Error loading status history dates:", error);
    } finally {
      datesReady.value = true;
    }
  };

  const initializeStatuses = async () => {
    try {
      statusOptions.value = await getIssueStatuses();
      const initialSelection = await getInitialIsTerminalIsPassedStatusTitle();
      // The status object that matches the initial title
      if (initialSelection && statusOptions.value.length > 0) {
        const initialStatusObject = statusOptions.value.find((status) => status.title === initialSelection);
        if (initialStatusObject) {
          selectedStatus.value = initialStatusObject;
        }
      }
    } finally {
      statusesReady.value = true;
    }
  };

  const cancelSettings = () => {
    tempStatusHistoryRange.value = [...statusHistoryRange.value];
    settingsDialog.value = false;
  };

  const applySettings = () => {
    statusHistoryRange.value = [...tempStatusHistoryRange.value];
    settingsDialog.value = false;
  };

  // The dialog opens on the range in force
  watch(settingsDialog, (newValue) => {
    if (newValue) {
      tempStatusHistoryRange.value = [...statusHistoryRange.value];
    }
  });

  watch(
    debouncedStatusHistoryRange,
    (newRange) => {
      if (unref(dataLoaded)) {
        applyStatusHistoryRangeFilter(newRange);
      }
    },
    { deep: true }
  );

  // Once the page's data is loaded: the statuses (+ the initial one), then the history dates —
  // the old adapter's two watchers, in their order
  watch(() => unref(dataLoaded), (loaded) => {
    if (!loaded) return;
    if (statusOptions.value.length === 0) initializeStatuses();
    if (statusHistoryDates.value.length === 0) loadStatusHistoryDates();
  }, { immediate: true });

  return {
    // spread onto BaseBoxPlotChart
    chart: {
      dataProviders: {
        issueCategories: getIssueCategoryData,
        issueTypes: getIssueTypeData,
        issueItems: getIssueItemData
      },
      contextUpdaters: {
        issueCategories: getIssueCategoryFilterContext,
        issueTypes: getIssueTypeFilterContext,
        reset: removeFilter
      },
      // RAW keys — the base translates them
      navigationConfig: [
        { level: 'issueCategories', titleKey: 'issueCategories' },
        { level: 'issueTypes', titleKey: 'issueTypes' },
        { level: 'issueItems', titleKey: 'issueItems' }
      ]
    },
    ready: computed(() => !!unref(dataLoaded) && statusesReady.value && datesReady.value),
    color: computed(() => selectedStatus.value?.color),
    // the status picker
    statusOptions,
    selectedStatus,
    setStatus,
    // the date-range dialog
    settingsDialog,
    statusHistoryDates,
    tempStatusHistoryRange,
    getDateFromIndex,
    applySettings,
    cancelSettings
  };
}
